import * as z from 'zod';

export const GetReferralRewardRequest = z.object({
  _id: z
    .string()
    .describe('Referral reward ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetReferralRewardResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Referral reward ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the referral reward is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the referral reward.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the referral reward was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the referral reward was last updated.')
      .optional()
      .nullable(),
    rewardType: z
      .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
      .describe('Type of reward given.')
      .optional(),
  }),
  z.intersection(
    z.xor([
      z.object({
        rewardedReferringCustomerId: z.never().optional(),
        rewardedReferredFriendId: z.never().optional(),
      }),
      z.object({
        rewardedReferredFriendId: z.never().optional(),
        rewardedReferringCustomerId: z
          .string()
          .describe('ID of the referring customer who received the reward.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
      }),
      z.object({
        rewardedReferringCustomerId: z.never().optional(),
        rewardedReferredFriendId: z
          .string()
          .describe('ID of the referred friend who received the reward.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
      }),
    ]),
    z.xor([
      z.object({
        coupon: z.never().optional(),
        loyaltyPoints: z.never().optional(),
      }),
      z.object({
        loyaltyPoints: z.never().optional(),
        coupon: z
          .object({
            _id: z
              .string()
              .describe(
                'Coupon ID. Example: `8934b045-7052-4a90-be2b-832c70afc9da`.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            code: z
              .string()
              .describe(
                'The code that customers can use to apply the coupon. Example: `6RFD2A3HSPXW`.'
              )
              .optional(),
            status: z
              .enum(['UNKNOWN', 'ACTIVE', 'APPLIED', 'DELETED'])
              .describe('Current status of the coupon.')
              .optional(),
            couponSpecification: z
              .intersection(
                z.object({
                  name: z
                    .string()
                    .describe('Coupon name.')
                    .min(1)
                    .max(50)
                    .optional(),
                  discountType: z
                    .enum([
                      'UNKNOWN',
                      'FIXED_AMOUNT',
                      'PERCENTAGE',
                      'FREE_SHIPPING',
                    ])
                    .describe('Coupon discount type.')
                    .optional(),
                  limitedToOneItem: z
                    .boolean()
                    .describe(
                      'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                    )
                    .optional()
                    .nullable(),
                  appliesToSubscriptions: z
                    .boolean()
                    .describe(
                      'Whether the coupon applies to subscription products.'
                    )
                    .optional()
                    .nullable(),
                  discountedCycleCount: z
                    .number()
                    .int()
                    .describe(
                      'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                    )
                    .optional()
                    .nullable(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      fixedAmountOptions: z.never().optional(),
                      percentageOptions: z.never().optional(),
                    }),
                    z.object({
                      percentageOptions: z.never().optional(),
                      fixedAmountOptions: z
                        .object({
                          amount: z
                            .number()
                            .describe(
                              'Amount of the discount as a fixed value.'
                            )
                            .min(0.01)
                            .optional(),
                        })
                        .describe('Options for fixed amount discount.'),
                    }),
                    z.object({
                      fixedAmountOptions: z.never().optional(),
                      percentageOptions: z
                        .object({
                          percentage: z
                            .number()
                            .describe('Percentage of discount.')
                            .min(0)
                            .max(100)
                            .optional(),
                        })
                        .describe('Options for percentage discounts.'),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      minimumSubtotal: z.never().optional(),
                      scope: z.never().optional(),
                    }),
                    z.object({
                      scope: z.never().optional(),
                      minimumSubtotal: z
                        .number()
                        .describe(
                          'Limit the coupon to carts with a subtotal above this number.'
                        ),
                    }),
                    z.object({
                      minimumSubtotal: z.never().optional(),
                      scope: z
                        .object({
                          namespace: z
                            .string()
                            .describe(
                              'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                            )
                            .optional(),
                          group: z
                            .object({
                              name: z
                                .string()
                                .describe('Name of the group.')
                                .optional(),
                              entityId: z
                                .string()
                                .describe('Entity ID of the group.')
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                            )
                            .optional(),
                        })
                        .describe(
                          'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                        ),
                    }),
                  ])
                )
              )
              .describe('Detailed specifications of the coupon.')
              .optional(),
          })
          .describe(
            'Details of a coupon reward. Present when `rewardType` is `COUPON`.'
          ),
      }),
      z.object({
        coupon: z.never().optional(),
        loyaltyPoints: z
          .object({
            transactionId: z
              .string()
              .describe('Loyalty transaction ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            amount: z
              .number()
              .int()
              .describe('The number of loyalty points awarded.')
              .optional(),
          })
          .describe(
            'Details of a loyalty points reward. Present when `rewardType` is `LOYALTY_POINTS`.'
          ),
      }),
    ])
  )
);
export const QueryReferralRewardsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          rewardedReferringCustomerId: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          rewardedReferredFriendId: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          rewardType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'rewardedReferringCustomerId',
                'rewardedReferredFriendId',
                'rewardType',
                '_createdDate',
                '_updatedDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query to filter referral rewards.'),
  options: z
    .object({
      contactId: z
        .string()
        .describe(
          'Contact ID to filter rewards by. Use `"me"` for current identity\'s rewards.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const QueryReferralRewardsResponse = z.object({
  referralRewards: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Referral reward ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the referral reward is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the referral reward.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the referral reward was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the referral reward was last updated.')
            .optional()
            .nullable(),
          rewardType: z
            .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
            .describe('Type of reward given.')
            .optional(),
        }),
        z.intersection(
          z.xor([
            z.object({
              rewardedReferringCustomerId: z.never().optional(),
              rewardedReferredFriendId: z.never().optional(),
            }),
            z.object({
              rewardedReferredFriendId: z.never().optional(),
              rewardedReferringCustomerId: z
                .string()
                .describe(
                  'ID of the referring customer who received the reward.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              rewardedReferringCustomerId: z.never().optional(),
              rewardedReferredFriendId: z
                .string()
                .describe('ID of the referred friend who received the reward.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ]),
          z.xor([
            z.object({
              coupon: z.never().optional(),
              loyaltyPoints: z.never().optional(),
            }),
            z.object({
              loyaltyPoints: z.never().optional(),
              coupon: z
                .object({
                  _id: z
                    .string()
                    .describe(
                      'Coupon ID. Example: `8934b045-7052-4a90-be2b-832c70afc9da`.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  code: z
                    .string()
                    .describe(
                      'The code that customers can use to apply the coupon. Example: `6RFD2A3HSPXW`.'
                    )
                    .optional(),
                  status: z
                    .enum(['UNKNOWN', 'ACTIVE', 'APPLIED', 'DELETED'])
                    .describe('Current status of the coupon.')
                    .optional(),
                  couponSpecification: z
                    .intersection(
                      z.object({
                        name: z
                          .string()
                          .describe('Coupon name.')
                          .min(1)
                          .max(50)
                          .optional(),
                        discountType: z
                          .enum([
                            'UNKNOWN',
                            'FIXED_AMOUNT',
                            'PERCENTAGE',
                            'FREE_SHIPPING',
                          ])
                          .describe('Coupon discount type.')
                          .optional(),
                        limitedToOneItem: z
                          .boolean()
                          .describe(
                            'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                          )
                          .optional()
                          .nullable(),
                        appliesToSubscriptions: z
                          .boolean()
                          .describe(
                            'Whether the coupon applies to subscription products.'
                          )
                          .optional()
                          .nullable(),
                        discountedCycleCount: z
                          .number()
                          .int()
                          .describe(
                            'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                          )
                          .optional()
                          .nullable(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            fixedAmountOptions: z.never().optional(),
                            percentageOptions: z.never().optional(),
                          }),
                          z.object({
                            percentageOptions: z.never().optional(),
                            fixedAmountOptions: z
                              .object({
                                amount: z
                                  .number()
                                  .describe(
                                    'Amount of the discount as a fixed value.'
                                  )
                                  .min(0.01)
                                  .optional(),
                              })
                              .describe('Options for fixed amount discount.'),
                          }),
                          z.object({
                            fixedAmountOptions: z.never().optional(),
                            percentageOptions: z
                              .object({
                                percentage: z
                                  .number()
                                  .describe('Percentage of discount.')
                                  .min(0)
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Options for percentage discounts.'),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            minimumSubtotal: z.never().optional(),
                            scope: z.never().optional(),
                          }),
                          z.object({
                            scope: z.never().optional(),
                            minimumSubtotal: z
                              .number()
                              .describe(
                                'Limit the coupon to carts with a subtotal above this number.'
                              ),
                          }),
                          z.object({
                            minimumSubtotal: z.never().optional(),
                            scope: z
                              .object({
                                namespace: z
                                  .string()
                                  .describe(
                                    'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                                  )
                                  .optional(),
                                group: z
                                  .object({
                                    name: z
                                      .string()
                                      .describe('Name of the group.')
                                      .optional(),
                                    entityId: z
                                      .string()
                                      .describe('Entity ID of the group.')
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                              ),
                          }),
                        ])
                      )
                    )
                    .describe('Detailed specifications of the coupon.')
                    .optional(),
                })
                .describe(
                  'Details of a coupon reward. Present when `rewardType` is `COUPON`.'
                ),
            }),
            z.object({
              coupon: z.never().optional(),
              loyaltyPoints: z
                .object({
                  transactionId: z
                    .string()
                    .describe('Loyalty transaction ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  amount: z
                    .number()
                    .int()
                    .describe('The number of loyalty points awarded.')
                    .optional(),
                })
                .describe(
                  'Details of a loyalty points reward. Present when `rewardType` is `LOYALTY_POINTS`.'
                ),
            }),
          ])
        )
      )
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for paging.')
    .optional(),
});
